import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaWhatsapp,
} from "react-icons/fa";
import {
  FiEdit2,
  FiLogOut,
  FiMapPin,
  FiPhone,
  FiPlay,
  FiSettings,
  FiShield,
  FiVideo,
} from "react-icons/fi";
import { BsPatchCheckFill } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/contexts/AuthContext";
import {
  getOnboardingResumePath,
  isOnboardingIncomplete,
} from "../onboarding/onboardingSession";
import {
  loadOwnReels,
  MAX_OWN_REELS,
  OWN_REELS_UPDATED_EVENT,
} from "../reels/ownReelsModel";
import {
  filterValidPhotos,
  fetchOwnProfile,
  getProfileContactSummary,
  getProfileDisplayName,
  getProfileLocationLabel,
  loadOwnProfile,
  cacheProfileLocally,
  normalizeStoredProfile,
  notifyProfileUpdated,
  PROFILE_PLACEHOLDER_IMAGE,
  PROFILE_UPDATED_EVENT,
} from "./profileModel";
import ProfileSettingsMenu from "./ProfileSettingsMenu";
import ProfileLocationSheet from "./ProfileLocationSheet";
import "./ProfileDetails.css";
import "./ProfileLocationSheet.css";

const ONBOARDING_EDIT_START = "/onboarding/photos";
const PHOTO_SWIPE_THRESHOLD = 0.18;
const PHOTO_TAP_MAX_MOVE = 12;

function ReelProgressRing({ count, max = MAX_OWN_REELS, size = 42 }) {
  const progress = Math.min(Math.max(count / max, 0), 1);
  const strokeWidth = 2.75;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress);
  const center = size / 2;

  return (
    <svg
      className="profile-dashboard__reel-progress"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="rgba(196, 165, 116, 0.28)"
        strokeWidth={strokeWidth}
      />
      {progress > 0 ? (
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--accent-strong)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      ) : null}
    </svg>
  );
}

function ProfileSection({ title, children }) {
  return (
    <section className="profile-dashboard__section">
      <h2 className="profile-dashboard__section-title">{title}</h2>
      <div className="profile-dashboard__section-body">{children}</div>
    </section>
  );
}

function ChipList({ items, emptyLabel }) {
  if (!items?.length) {
    return <p className="profile-dashboard__empty">{emptyLabel}</p>;
  }

  return (
    <div className="profile-dashboard__chips">
      {items.map((item) => (
        <span key={item} className="profile-dashboard__chip">{item}</span>
      ))}
    </div>
  );
}

function LanguageStars({ level = 0 }) {
  return (
    <span className="profile-dashboard__language-stars" aria-hidden>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={
            star <= level
              ? "profile-dashboard__language-star profile-dashboard__language-star--filled"
              : "profile-dashboard__language-star"
          }
        >
          ★
        </span>
      ))}
    </span>
  );
}

function LanguageList({ languages, emptyLabel }) {
  if (!languages?.length) {
    return <p className="profile-dashboard__empty">{emptyLabel}</p>;
  }

  return (
    <ul className="profile-dashboard__language-list">
      {languages.map((language) => (
        <li key={language.name} className="profile-dashboard__language-row">
          <span className="profile-dashboard__language-name">{language.name}</span>
          <LanguageStars level={language.stars ?? 0} />
        </li>
      ))}
    </ul>
  );
}

export default function ProfileDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isRestoring, logout } = useAuth();
  const [profile, setProfile] = useState(() => loadOwnProfile(user?.id));
  const [reelCount, setReelCount] = useState(() => loadOwnReels().length);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [dragPx, setDragPx] = useState(0);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [locationSheetOpen, setLocationSheetOpen] = useState(false);
  const settingsBtnRef = useRef(null);
  const heroRef = useRef(null);
  const photoDragRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    startIndex: 0,
    locked: false,
  });

  const reloadProfile = useCallback(async () => {
    const nextProfile = await fetchOwnProfile(user?.id);
    setProfile(nextProfile);
  }, [user?.id]);

  useEffect(() => {
    const syncReels = () => setReelCount(loadOwnReels().length);
    syncReels();
    window.addEventListener(OWN_REELS_UPDATED_EVENT, syncReels);
    return () => window.removeEventListener(OWN_REELS_UPDATED_EVENT, syncReels);
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith("/profile-details") || isRestoring) return;

    if (isAuthenticated && !user?.onboardingComplete && isOnboardingIncomplete(isAuthenticated, user)) {
      navigate(getOnboardingResumePath(), { replace: true });
    }
  }, [isAuthenticated, isRestoring, navigate, location.pathname, user?.onboardingComplete]);

  useEffect(() => {
    if (location.state?.freshProfile) return;
    reloadProfile();
  }, [location.pathname, location.key, location.state?.freshProfile, reloadProfile]);

  useEffect(() => {
    const onProfileUpdated = () => reloadProfile();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, [reloadProfile]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) reloadProfile();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [reloadProfile]);

  const photos = filterValidPhotos(profile.photos);
  const heroPhotos = photos.length ? photos : [PROFILE_PLACEHOLDER_IMAGE];
  const safePhotoIndex = Math.min(photoIndex, heroPhotos.length - 1);
  const hasMultiplePhotos = heroPhotos.length > 1;
  const contact = getProfileContactSummary(profile);
  const categories = profile.profileTypes?.length
    ? profile.profileTypes
    : profile.category
      ? [profile.category]
      : [];

  useEffect(() => {
    setPhotoIndex(0);
  }, [photos.length]);

  const openProfileEditor = () => {
    navigate(ONBOARDING_EDIT_START, { state: { mode: "edit" } });
  };

  const finishPhotoDrag = useCallback((clientX) => {
    const drag = photoDragRef.current;
    const width = heroRef.current?.clientWidth ?? 1;
    const delta = clientX - drag.startX;
    const threshold = width * PHOTO_SWIPE_THRESHOLD;

    setIsDraggingPhoto(false);
    setDragPx(0);
    photoDragRef.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      startIndex: 0,
      locked: false,
    };

    if (Math.abs(delta) < PHOTO_TAP_MAX_MOVE) {
      const rect = heroRef.current?.getBoundingClientRect();
      if (rect) {
        const tapX = clientX - rect.left;
        if (tapX < rect.width * 0.34) {
          setPhotoIndex((prev) => (prev === 0 ? heroPhotos.length - 1 : prev - 1));
          return;
        }
        if (tapX > rect.width * 0.66) {
          setPhotoIndex((prev) => (prev === heroPhotos.length - 1 ? 0 : prev + 1));
        }
      }
      return;
    }

    if (delta <= -threshold) {
      setPhotoIndex((prev) => Math.min(prev + 1, heroPhotos.length - 1));
      return;
    }

    if (delta >= threshold) {
      setPhotoIndex((prev) => Math.max(prev - 1, 0));
    }
  }, [heroPhotos.length]);

  const handleHeroPointerDown = (event) => {
    if (!hasMultiplePhotos || (event.pointerType === "mouse" && event.button !== 0)) return;

    photoDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startIndex: safePhotoIndex,
      locked: false,
    };
    setDragPx(0);
  };

  const handleHeroPointerMove = (event) => {
    const drag = photoDragRef.current;
    if (drag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;

    if (!drag.locked) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        photoDragRef.current = {
          pointerId: null,
          startX: 0,
          startY: 0,
          startIndex: 0,
          locked: false,
        };
        return;
      }

      drag.locked = true;
      setIsDraggingPhoto(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    let dragOffset = deltaX;
    if (drag.startIndex === 0 && dragOffset > 0) {
      dragOffset *= 0.32;
    } else if (drag.startIndex === heroPhotos.length - 1 && dragOffset < 0) {
      dragOffset *= 0.32;
    }

    setDragPx(dragOffset);
    event.preventDefault();
  };

  const handleHeroPointerUp = (event) => {
    const drag = photoDragRef.current;
    if (drag.pointerId !== event.pointerId) return;

    if (drag.locked) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may already be released.
      }
    }

    if (drag.locked || Math.abs(event.clientX - drag.startX) < PHOTO_TAP_MAX_MOVE) {
      finishPhotoDrag(event.clientX);
      return;
    }

    photoDragRef.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      startIndex: 0,
      locked: false,
    };
  };

  const handleHeroPointerCancel = (event) => {
    const drag = photoDragRef.current;
    if (drag.pointerId !== event.pointerId) return;

    if (drag.locked) {
      finishPhotoDrag(event.clientX);
      return;
    }

    photoDragRef.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      startIndex: 0,
      locked: false,
    };
    setIsDraggingPhoto(false);
    setDragPx(0);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/auth", { replace: true, state: { navTab: "profile", instantAuth: true } });
  };

  const handleAddVideo = () => {
    navigate("/manage-reels");
  };

  const reelSubcopy = reelCount > 0
    ? `${reelCount} of ${MAX_OWN_REELS} uploaded`
    : "Show your vibe";

  const trackTransform = hasMultiplePhotos
    ? `translate3d(calc(-${safePhotoIndex * 100}% + ${dragPx}px), 0, 0)`
    : "translate3d(0, 0, 0)";

  return (
    <div className="profile-dashboard">
      <div
        ref={heroRef}
        className={`profile-dashboard__hero${
          isDraggingPhoto ? " profile-dashboard__hero--dragging" : ""
        }${hasMultiplePhotos ? " profile-dashboard__hero--gallery" : ""}`}
      >
        <div className="profile-dashboard__hero-slider">
          <div
            className={`profile-dashboard__hero-track${
              isDraggingPhoto ? " profile-dashboard__hero-track--dragging" : ""
            }`}
            style={{ transform: trackTransform }}
          >
            {heroPhotos.map((photo, index) => (
              <img
                key={`${photo}-${index}`}
                src={photo}
                alt={index === 0 ? getProfileDisplayName(profile) : ""}
                className="profile-dashboard__hero-slide"
                decoding="async"
                draggable={false}
              />
            ))}
          </div>
        </div>
        <div className="profile-dashboard__hero-gradient" aria-hidden />

        {hasMultiplePhotos ? (
          <div
            className="profile-dashboard__hero-dots"
            aria-hidden
            onPointerDown={(event) => event.stopPropagation()}
          >
            {heroPhotos.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`profile-dashboard__hero-dot${
                  index === safePhotoIndex ? " profile-dashboard__hero-dot--active" : ""
                }`}
                aria-label={`Show photo ${index + 1}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setPhotoIndex(index);
                }}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="profile-dashboard__hero-top">
        <button
          type="button"
          className="profile-dashboard__hero-pill-btn"
          onClick={openProfileEditor}
        >
          <FiEdit2 aria-hidden />
          <span>Edit profile</span>
        </button>
        <button
          type="button"
          ref={settingsBtnRef}
          className={`profile-dashboard__icon-btn${
            settingsMenuOpen ? " profile-dashboard__icon-btn--active" : ""
          }`}
          aria-label="Settings"
          aria-expanded={settingsMenuOpen}
          aria-haspopup="menu"
          onClick={() => setSettingsMenuOpen((open) => !open)}
        >
          <FiSettings />
        </button>
      </div>

      <ProfileSettingsMenu
        open={settingsMenuOpen}
        onClose={() => setSettingsMenuOpen(false)}
        anchorRef={settingsBtnRef}
      />

      <ProfileLocationSheet
        open={locationSheetOpen}
        onClose={() => setLocationSheetOpen(false)}
        profile={profile}
        onUpdated={(updatedProfile) => {
          const normalized = normalizeStoredProfile(updatedProfile, user?.id);
          cacheProfileLocally(normalized);
          setProfile(normalized);
          notifyProfileUpdated();
        }}
      />

      <div className="profile-dashboard__scroll">
        <div
          className={`profile-dashboard__scroll-spacer${
            hasMultiplePhotos ? " profile-dashboard__scroll-spacer--gallery" : ""
          }${isDraggingPhoto ? " profile-dashboard__scroll-spacer--dragging" : ""}`}
          aria-hidden={!hasMultiplePhotos}
          aria-label={
            hasMultiplePhotos
              ? `Photo ${safePhotoIndex + 1} of ${heroPhotos.length}. Swipe or drag to browse.`
              : undefined
          }
          onPointerDown={handleHeroPointerDown}
          onPointerMove={handleHeroPointerMove}
          onPointerUp={handleHeroPointerUp}
          onPointerCancel={handleHeroPointerCancel}
        />

        <div className="profile-dashboard__card">
        <div className="profile-dashboard__identity">
          <div className="profile-dashboard__identity-main">
            <h1 className="profile-dashboard__name">{getProfileDisplayName(profile)}</h1>
            <button
              type="button"
              className="profile-dashboard__location profile-dashboard__location--button"
              onClick={() => setLocationSheetOpen(true)}
              aria-label="Manage your location"
            >
              <FiMapPin aria-hidden />
              <span>{getProfileLocationLabel(profile)}</span>
              {profile.locationSource === "gps" ? (
                <span className="profile-dashboard__location-badge">Live</span>
              ) : null}
            </button>
          </div>

          <button
            type="button"
            className="profile-dashboard__reel-btn"
            aria-label={`Add reel, ${reelCount} of ${MAX_OWN_REELS} uploaded`}
            onClick={handleAddVideo}
          >
            <span className="profile-dashboard__reel-btn-icon">
              <ReelProgressRing count={reelCount} />
              <FiPlay aria-hidden />
            </span>
            <span className="profile-dashboard__reel-btn-copy">
              <span className="profile-dashboard__reel-btn-title">Add reel</span>
              <span className="profile-dashboard__reel-btn-sub">{reelSubcopy}</span>
            </span>
          </button>
        </div>

        <div className="profile-dashboard__identity-meta">
          <div className="profile-dashboard__badges">
            {profile.verified ? (
              <span className="profile-dashboard__badge profile-dashboard__badge--verified">
                <BsPatchCheckFill aria-hidden />
                Verified
              </span>
            ) : (
              <span className="profile-dashboard__badge profile-dashboard__badge--verify-id">
                <FiShield aria-hidden />
                ID verification
              </span>
            )}
          </div>
        </div>

        <ProfileSection title="Bio">
          <p className="profile-dashboard__bio">
            {profile.bio?.trim() || "Tell people a little about yourself."}
          </p>
        </ProfileSection>

        <ProfileSection title="Languages">
          <LanguageList
            languages={profile.languages}
            emptyLabel="Add the languages you speak."
          />
        </ProfileSection>

        <ProfileSection title="Category">
          <ChipList items={categories} emptyLabel="Choose your profile category." />
        </ProfileSection>

        <ProfileSection title="Service for">
          <ChipList items={profile.serviceFor} emptyLabel="Choose who you offer services to." />
        </ProfileSection>

        <ProfileSection title="Services">
          <ChipList items={profile.services} emptyLabel="Add the services you offer." />
        </ProfileSection>

        <ProfileSection title="Contact & availability">
          <div className="profile-dashboard__status-list">
            <div className="profile-dashboard__status-row">
              <span className="profile-dashboard__status-label">
                <FaWhatsapp aria-hidden />
                WhatsApp
              </span>
              <span
                className={`profile-dashboard__status-value${
                  contact.hasWhatsapp ? " profile-dashboard__status-value--on" : ""
                }`}
              >
                {contact.hasWhatsapp ? "Connected" : "Not set"}
              </span>
            </div>
            <div className="profile-dashboard__status-row">
              <span className="profile-dashboard__status-label">
                <FiPhone aria-hidden />
                Phone
              </span>
              <span
                className={`profile-dashboard__status-value${
                  contact.hasPhone ? " profile-dashboard__status-value--on" : ""
                }`}
              >
                {contact.hasPhone ? "Connected" : "Not set"}
              </span>
            </div>
            <div className="profile-dashboard__status-row">
              <span className="profile-dashboard__status-label">
                <FiVideo aria-hidden />
                Webcam
              </span>
              <span
                className={`profile-dashboard__status-value${
                  contact.hasWebcam ? " profile-dashboard__status-value--on" : ""
                }`}
              >
                {contact.hasWebcam ? "Available" : "Not available"}
              </span>
            </div>
          </div>
        </ProfileSection>

        <div className="profile-dashboard__footer-actions">
          <button
            type="button"
            className="profile-dashboard__logout-btn"
            onClick={handleLogout}
          >
            <FiLogOut aria-hidden />
            <span>Log out</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
